
export const MCP_CONNECT_STEPS = {
  title: "Instructions",
  steps: [
    "Click Create API Key, choose the tools you want to allow, and generate it.",
    "Copy the MCP URL shown once after creation. It already includes your key.",
    "In Claude, open Settings > Connectors > Add custom connector, paste the MCP URL and save. Other AI platforms that support MCP work the same way: add a remote MCP server and paste the URL.",
    "Start a new chat, enable the LawGPT connector and ask for a task, such as searching a statute or drafting an application.",
  ],
};

export const MCP_TEXT = {
  PAGE_TITLE: "MCP Connector",
  PAGE_SUBTITLE:
    "Create API keys that let AI assistants such as Claude use LawGPT tools on your behalf.",
  SECURITY_NOTE:
    "Treat an API key like a password. Anyone who has it can use the tools you granted to it, with your account. Delete a key if you think it has leaked.",
  CREATE_BUTTON: "Create API Key",
  EMPTY_STATE: "No API key generated.",
  EMPTY_HINT: "Generate one to connect AI platforms such as Claude.",
  LOAD_ERROR: "Could not load your API keys. Please try again.",
  NETWORK_ERROR:
    "Could not reach the AI service. It may be starting up. Please try again in a moment.",
  LOADING: "Loading your API keys...",
  RETRY: "Retry",
  REFRESH: "Refresh",
  USER_MISSING: "Please log in again to manage MCP API keys.",
  COLUMN_NAME: "Name",
  COLUMN_TOOLS: "Selected tools",
  COLUMN_CREATED: "Created",
  COLUMN_STATUS: "Status",
  COLUMN_ACTIONS: "Actions",
  NO_TOOLS: "No tools",
  STATUS_ACTIVE: "Active",
  STATUS_EXPIRED: "Expired",
  VIEW_DETAILS: "Details",
  DELETE: "Delete",
  CREATE_SUCCESS: "API key created.",
  CREATE_FAILED: "Could not create the API key.",
  DELETE_SUCCESS: "API key deleted.",
  DELETE_FAILED: "Could not delete the API key.",
  COPIED: "Copied to clipboard.",
  COPY_FAILED: "Could not copy. Please copy it manually.",
};

export const MCP_CREATE_LABELS = {
  title: "Create API Key",
  nameLabel: "Key name",
  namePlaceholder: "For example: Claude desktop",
  nameRequired: "Enter a name for the key.",
  expiryLabel: "Expires after",
  toolsTitle: "Tools this key can use",
  toolsRequired: "Select at least one tool.",
  toolsLoading: "Loading available tools...",
  toolsFallback:
    "Could not load the live tool list, showing the default tools.",
  readOnly: "Read only",
  selectAll: "Select all",
  clearAll: "Clear all",
  cancel: "Cancel",
  submit: "Generate key",
  submitting: "Generating...",
};

export const MCP_CREATED_LABELS = {
  title: "Your new API key",
  warningTitle: "Copy your key now: it is shown only once",
  warning:
    "This key is shown only once and cannot be retrieved later. Copy it now and store it somewhere safe. If you lose it, delete the key and create a new one.",
  hint: "To connect Claude: open Settings, then Connectors, choose Add custom connector, and paste the MCP URL. No separate sign-in is needed because the URL already contains your key, so do not share it.",
  keyLabel: "API key",
  mcpUrlLabel: "MCP URL (includes your key)",
  copy: "Copy",
  done: "I have copied it",
};

export const MCP_DETAILS_LABELS = {
  title: "API key details",
  detailsTitle: "Details",
  fieldName: "Name",
  fieldKey: "Key",
  fieldStatus: "Status",
  statusActive: "Active",
  statusExpired: "Expired",
  fieldCreated: "Created",
  fieldExpires: "Expires",
  fieldLastUsed: "Last used",
  toolsTitle: "Selected tools",
  noTools: "This key has no tools.",
  detailsLoading: "Loading key details...",
  detailsFailed: "Could not load the details of this key.",
  tracesTitle: "Usage history",
  tracesLoading: "Loading usage history...",
  tracesEmpty: "This key has not been used yet.",
  tracesFailed: "Could not load the usage history.",
  traceTime: "Time",
  traceTool: "Tool",
  traceStatus: "Result",
  traceDuration: "Duration",
  traceError: "Error",
  never: "Never",
  retry: "Retry",
  close: "Close",
};

export const MCP_DELETE_LABELS = {
  title: "Delete API key",
  confirm: "Delete",
  cancel: "Cancel",
};

export const MCP_EXPIRY_OPTIONS = [
  { label: "7 days", value: 7 },
  { label: "30 days", value: 30 },
  { label: "90 days", value: 90 },
  { label: "1 year", value: 365 },
  { label: "Never", value: null },
];

export const MCP_DEFAULT_EXPIRY_DAYS = 30;

export const MCP_DEFAULT_TOOLS = [
  "list_application_templates",
  "get_application_template_fields",
  "draft_application",
  "save_application_draft",
  "list_application_drafts",
  "list_petition_templates",
  "get_petition_template_fields",
  "draft_petition",
  "save_petition_draft",
  "list_petition_drafts",
  "search_statutes",
];

const GROUP_RULES = [
  { test: /application/i, label: "Application drafting" },
  { test: /petition/i, label: "Petition drafting" },
  { test: /statute/i, label: "Statute search" },
  { test: /case|lawyer|client|task|document|cms/i, label: "Case management" },
];

export const MCP_OTHER_GROUP = "Other";

export const toolGroupOf = (name = "") =>
  GROUP_RULES.find((rule) => rule.test.test(name))?.label || MCP_OTHER_GROUP;

export const humanizeToolName = (name = "") =>
  name.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

const toArray = (value) => (Array.isArray(value) ? value : []);

const toolNameOf = (tool) => (typeof tool === "string" ? tool : tool?.name);

export const normalizeToolCatalog = (data) => {
  const collected = [];

  const add = (item, moduleName) => {
    const name = toolNameOf(item);
    if (!name || collected.some((t) => t.name === name)) return;

    const isObject = item && typeof item === "object";
    if (isObject && item.enabled === false) return;

    collected.push({
      name,
      title: (isObject && item.title) || "",
      description: (isObject && item.description) || "",
      group: moduleName ? humanizeToolName(String(moduleName)) : toolGroupOf(name),
      readOnly: isObject && item.read_only === true,
    });
  };

  const walk = (node, moduleName) => {
    if (Array.isArray(node)) {
      node.forEach((item) => {
        if (item && typeof item === "object" && Array.isArray(item.tools)) {
          walk(item.tools, item.title || item.name || item.module || item.id || moduleName);
        } else {
          add(item, moduleName);
        }
      });
    } else if (node && typeof node === "object") {
      if (Array.isArray(node.tools)) walk(node.tools, moduleName);
      if (Array.isArray(node.modules)) walk(node.modules, moduleName);
    }
  };

  walk(data);

  return collected.length
    ? collected
    : MCP_DEFAULT_TOOLS.map((name) => ({
        name,
        title: "",
        description: "",
        group: toolGroupOf(name),
        readOnly: false,
      }));
};

export const groupTools = (tools) =>
  tools.reduce((groups, tool) => {
    (groups[tool.group] = groups[tool.group] || []).push(tool);
    return groups;
  }, {});

export const normalizeKeyList = (data) =>
  toArray(data).length
    ? data
    : toArray(data?.keys || data?.items || data?.data || data?.api_keys);

export const normalizeApiKey = (raw = {}, now = new Date()) => {
  const expiresAt = raw.expires_at || raw.expiresAt || null;
  const status = String(raw.status || "").toLowerCase();
  const isExpired =
    /expired/.test(status) ||
    Boolean(expiresAt && new Date(expiresAt).getTime() < now.getTime());

  const tools = toArray(raw.tools).map(toolNameOf).filter(Boolean);
  return {
    id: raw.id || raw._id || raw.key_id,
    name: raw.name || "",
    keyPrefix: raw.key_prefix || raw.keyPrefix || "",
    tools,
    toolCount: raw.tool_count ?? raw.toolCount ?? tools.length,
    createdAt: raw.created_at || raw.createdAt || null,
    expiresAt,
    lastUsedAt: raw.last_used_at || raw.lastUsedAt || null,
    isExpired,
  };
};

export const formatMcpDate = (value, fallback = "") => {
  if (!value) return fallback;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? fallback
    : date.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

export const normalizeCreatedKey = (raw = {}) => ({
  ...normalizeApiKey(raw),
  apiKey: raw.api_key || raw.apiKey || "",
  mcpUrl: raw.connector_url || raw.connectorUrl || raw.mcp_url || raw.mcpUrl || "",
  serverUrl: raw.mcp_url || raw.mcpUrl || "",
});

export const normalizeTrace = (raw = {}, index = 0) => {
  const status = String(raw.status || raw.result || "").toLowerCase();
  const error = raw.error || raw.error_message || "";
  let ok = null;
  if (typeof raw.success === "boolean") ok = raw.success;
  else if (status) ok = !/(error|fail|denied|unauthor|reject)/.test(status);
  else if (error) ok = false;

  const duration = raw.duration_ms ?? raw.latency_ms ?? raw.duration ?? null;

  return {
    id: String(raw.id || raw._id || raw.trace_id || index),
    time: raw.created_at || raw.timestamp || raw.time || raw.called_at || null,
    tool: raw.tool || raw.tool_name || raw.name || "",
    ok,
    durationMs: duration === null || Number.isNaN(Number(duration)) ? null : Number(duration),
    error: typeof error === "string" ? error : JSON.stringify(error),
  };
};

export const normalizeTraceList = (data) => {
  const list = toArray(data).length
    ? data
    : toArray(data?.traces || data?.items || data?.data || data?.results);
  return list.map((raw, index) => normalizeTrace(raw, index));
};

export const copyTextToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

export const MCP_REQUEST_TIMEOUT_MS = 90000;

export const mcpErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  if (error?.response?.data?.message) return error.response.data.message;

  if (error && !error.response && (error.request || error.code)) {
    return MCP_TEXT.NETWORK_ERROR;
  }

  const status = error?.response?.status;
  return status ? `${fallback} (HTTP ${status})` : fallback;
};
